/**
 * FDV — motor de regalos del carrito (Núcleo · C1).
 * Ver docs/migration/15-carrito-regalos.md.
 *
 * Headless. Una sola instancia, global (se carga desde theme.liquid vía
 * snippets/fdv-gift-engine.liquid, patch #24).
 *
 * Qué hace:
 *   1. Lee las ofertas del <script id="fdv-gift-offers"> (horneado por Liquid
 *      desde el metaobjeto `fdv_gift_offer`).
 *   2. En cada `cartLinesUpdate` (Standard Events de Horizon) reconcilia las
 *      líneas de regalo: agrega las que deben estar, quita las que ya no.
 *   3. Publica el estado en `window.FDV.gifts.state` + evento `fdv:gifts:state`
 *      (contrato B — lo consume la barra de progreso C2 y, a futuro, los modales).
 *   4. Al agregar una línea le pone `properties._gift` / `_gift_label`
 *      (contrato A — lo consume el lock de línea C3, patch #26).
 *   5. Emite eventos CRO por el bus (`window.FDV.track`): fdv_gift_unlocked,
 *      fdv_gift_removed, fdv_gift_added_to_cart, fdv_promo_claimed, fdv_gift_error.
 *
 * NO toca DOM de UI. NO re-fetchea el HTML del carrito (usa el Section
 * Rendering de Horizon vía el CartLinesUpdateEvent).
 */

import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';
import { morphSection } from '@theme/section-renderer';

const DEBOUNCE_MS = 120;
const MAX_OPS_PER_CYCLE = 6;
const GIFT_PROP = '_gift';
const GIFT_LABEL_PROP = '_gift_label';

const log = (...a) => {
  if (window.FDV?.gifts?.debug) console.log('[fdv-gifts]', ...a);
};
const warn = (...a) => console.warn('[fdv-gifts]', ...a);

/* ------------------------------------------------------------------ config -- */

function readOffers() {
  const el = document.getElementById('fdv-gift-offers');
  if (!el) return [];
  try {
    const raw = JSON.parse(el.textContent || '{}');
    const offers = Array.isArray(raw) ? raw : raw.offers;
    return Array.isArray(offers) ? offers.map(normalizeOffer) : [];
  } catch (e) {
    warn('no se pudo parsear #fdv-gift-offers', e);
    return [];
  }
}

function readBootstrap() {
  const el = document.getElementById('fdv-gift-offers');
  if (!el) return {};
  try {
    const raw = JSON.parse(el.textContent || '{}');
    return raw && !Array.isArray(raw) ? raw.bootstrap || {} : {};
  } catch {
    return {};
  }
}

/** Normaliza tipos (montos a centavos, ids a Number, defaults). */
function normalizeOffer(o) {
  const num = (v) => (v == null || v === '' ? null : Number(v));
  return {
    id: String(o.id ?? o.handle ?? o.internal_name ?? Math.random().toString(36).slice(2)),
    internalName: o.internal_name || o.id || '',
    priority: num(o.priority) ?? 100,
    triggerType: o.trigger_type || 'cart_subtotal',
    thresholdCents: o.threshold_amount != null && o.threshold_amount !== '' ? Math.round(Number(o.threshold_amount) * 100) : null,
    thresholdQty: num(o.threshold_quantity),
    eligibleCollection: o.eligible_collection
      ? { id: num(o.eligible_collection.id), productIds: (o.eligible_collection.product_ids || []).map(Number) }
      : null,
    triggerProductId: num(o.trigger_product_id ?? o.trigger_product?.id),
    discountCode: (o.discount_code || '').trim().toUpperCase() || null,
    urlParam: (o.url_param || '').trim() || null,
    giftVariantId: num(o.gift_variant?.id ?? o.gift_variant_id),
    giftVariantPrice: num(o.gift_variant?.price),
    giftQty: num(o.gift_quantity) || 1,
    matchTriggerQty: o.match_trigger_quantity === true,
    mechanism: o.mechanism || 'zero_price',
    autoDiscountTitle: (o.auto_discount_title || '').trim().toUpperCase() || null,
    startAt: num(o.start_at), // epoch seconds; Liquid ya filtró, esto es defensa
    endAt: num(o.end_at),
    unlockedHeadline: o.unlocked_headline || '',
    progressText: o.progress_text || '',
    rewardLabel: (o.reward_label || '').trim(),
    giftLineLabel: o.gift_line_label || '✨ Producto de regalo',
    stackable: o.stackable !== false,
    mystery: o.mystery === true, // "regalo sorpresa": no se nombra el premio en la barra
    nearPct: num(o.near_pct) ?? 80,
    // Campos de superficie promocional — el motor los ignora; los consume
    // `fdv-offer-state.js` (cerebro de ofertas, doc 16 §3). Passthrough.
    promoSurface: o.promo_surface === true,
    promoHeadline: o.promo_headline || '',
    promoBody: o.promo_body || '',
    promoCtaLabel: o.promo_cta_label || '',
    promoCtaUrl: o.promo_cta_url || '',
    promoImage: o.promo_image || '',
  };
}

/* -------------------------------------------------------------- evaluación -- */

const inCollection = (offer, productId) =>
  !offer.eligibleCollection || offer.eligibleCollection.productIds.includes(Number(productId));

/**
 * Suma en centavos del carrito (opcionalmente acotada a la colección de la oferta).
 *
 * Para el carrito completo se usa `merchSubtotal()` = subtotal de mercancía
 * **después de TODOS los descuentos** (de línea + códigos de nivel carrito) y
 * **sin envío**, que es la base contra la que Shopify evalúa el umbral de la
 * tarifa de envío gratis en el checkout.
 *
 * ⚠️ NO usar `cart.total_price`: si el cliente ya llegó al paso de envío del
 * checkout y regresa al carrito, Shopify le SUMA la tarifa elegida ($139) a
 * `total_price`. Un carrito de $599.50 leía $738.50 → la barra decía "envío
 * gratis desbloqueado" y el checkout cobraba el envío. Tampoco `items_subtotal_price`
 * a secas: es pre-código de descuento de nivel carrito.
 */
function merchSubtotal(cart) {
  const items = cart.items || [];
  if (!items.length) return 0;
  const lines = items.reduce((sum, it) => sum + (it.final_line_price ?? it.line_price ?? 0), 0);
  const cartDiscounts = (cart.cart_level_discount_applications || []).reduce(
    (sum, app) => (app.target_type === 'shipping_line' ? sum : sum + (app.total_allocated_amount || 0)),
    0
  );
  return Math.max(0, lines - cartDiscounts);
}

function subtotalFor(offer, cart) {
  if (!offer.eligibleCollection) return merchSubtotal(cart);
  return cart.items
    .filter((it) => inCollection(offer, it.product_id) && !isGiftLine(it))
    .reduce((sum, it) => sum + (it.final_line_price ?? it.line_price ?? 0), 0);
}

function collectionQtyFor(offer, cart) {
  return cart.items
    .filter((it) => inCollection(offer, it.product_id) && !isGiftLine(it))
    .reduce((sum, it) => sum + it.quantity, 0);
}

function triggerProductQty(offer, cart) {
  return cart.items
    .filter((it) => Number(it.product_id) === offer.triggerProductId && !isGiftLine(it))
    .reduce((sum, it) => sum + it.quantity, 0);
}

/** ¿Hay un código de descuento aplicado que coincida con el título de la oferta? */
function discountCodeApplied(offer, cart) {
  const titles = [];
  for (const app of cart.cart_level_discount_applications || []) {
    if (app.discount_application?.type === 'discount_code' || app.type === 'discount_code') {
      titles.push((app.title || app.discount_application?.title || '').toUpperCase());
    }
  }
  for (const it of cart.items || []) {
    for (const alloc of it.line_level_discount_allocations || []) {
      if (alloc.discount_application?.type === 'discount_code') {
        titles.push((alloc.discount_application.title || '').toUpperCase());
      }
    }
  }
  return titles.includes(offer.discountCode);
}

const isGiftLine = (item) => !!(item.properties && item.properties[GIFT_PROP]);

/** URL param persistido en sessionStorage mientras dure la pestaña. */
function urlParamActive(offer) {
  if (!offer.urlParam) return false;
  const key = `fdv_gift_url:${offer.urlParam}`;
  try {
    if (new URLSearchParams(location.search).toString().toLowerCase().includes(offer.urlParam.toLowerCase()) ||
        location.href.toLowerCase().includes(offer.urlParam.toLowerCase())) {
      sessionStorage.setItem(key, '1');
    }
    return sessionStorage.getItem(key) === '1';
  } catch {
    return location.href.toLowerCase().includes(offer.urlParam.toLowerCase());
  }
}

function clearUrlParam(offer) {
  if (!offer.urlParam) return;
  try {
    sessionStorage.removeItem(`fdv_gift_url:${offer.urlParam}`);
  } catch {
    /* noop */
  }
}

/**
 * Estado puro por oferta a partir del carrito. Sin efectos secundarios
 * (salvo el sello de sessionStorage para url_param). Testeable.
 */
function evaluate(offers, cart) {
  const now = Date.now() / 1000;
  return offers
    // `promo_only` = oferta que solo alimenta superficies promocionales
    // (`fdv-offer-state.js`); no tiene mecánica de regalo → fuera del motor.
    .filter((o) => o.triggerType !== 'promo_only' && (!o.startAt || o.startAt <= now) && (!o.endAt || o.endAt >= now))
    .map((offer) => {
      const kind = offer.triggerType === 'cart_subtotal' || offer.triggerType === 'collection_quantity' ? 'threshold' : 'binary';
      let progress = 0;
      let remainingAmount = 0;
      let remainingQty = 0;
      let threshold = null;
      let qualifies = false;

      if (offer.triggerType === 'cart_subtotal') {
        threshold = offer.thresholdCents ?? 0;
        const cur = subtotalFor(offer, cart);
        progress = threshold > 0 ? Math.min(1, cur / threshold) : 1;
        remainingAmount = Math.max(0, threshold - cur);
        qualifies = cur >= threshold && threshold > 0;
      } else if (offer.triggerType === 'collection_quantity') {
        threshold = offer.thresholdQty ?? 0;
        const cur = collectionQtyFor(offer, cart);
        progress = threshold > 0 ? Math.min(1, cur / threshold) : 1;
        remainingQty = Math.max(0, threshold - cur);
        qualifies = cur >= threshold && threshold > 0;
      } else if (offer.triggerType === 'product_in_cart') {
        qualifies = triggerProductQty(offer, cart) > 0;
        progress = qualifies ? 1 : 0;
      } else if (offer.triggerType === 'discount_code') {
        qualifies = discountCodeApplied(offer, cart);
        progress = qualifies ? 1 : 0;
      } else if (offer.triggerType === 'url_param') {
        qualifies = urlParamActive(offer);
        progress = qualifies ? 1 : 0;
      }

      let status = 'locked';
      if (qualifies) status = 'unlocked';
      else if (kind === 'threshold' && progress * 100 >= offer.nearPct) status = 'near';

      const giftQty = offer.matchTriggerQty ? triggerProductQty(offer, cart) || 0 : offer.giftQty;

      return {
        id: offer.id,
        internalName: offer.internalName,
        kind,
        status,
        progress,
        remainingAmount,
        remainingQty,
        threshold,
        giftVariantId: offer.giftVariantId,
        giftQty,
        mechanism: offer.mechanism,
        priority: offer.priority,
        stackable: offer.stackable,
        mystery: offer.mystery,
        unlockedHeadline: offer.unlockedHeadline,
        progressText: renderProgressText(offer.progressText, { remainingAmount, remainingQty, threshold }),
        rewardLabel: offer.rewardLabel,
        giftLineLabel: offer.giftLineLabel,
        autoDiscountTitle: offer.autoDiscountTitle,
        discountApplied: null, // se rellena tras reconciliar (solo auto_discount)
        error: null,
        _offer: offer,
      };
    })
    .sort((a, b) => a.priority - b.priority);
}

function renderProgressText(tpl, { remainingAmount, remainingQty, threshold }) {
  if (!tpl) return '';
  return tpl
    .replace(/\{\{\s*restante\s*\}\}/g, formatMoney(remainingAmount))
    .replace(/\{\{\s*restante_cantidad\s*\}\}/g, String(remainingQty))
    .replace(/\{\{\s*meta\s*\}\}/g, threshold != null ? formatMoney(threshold) : '');
}

function formatMoney(cents) {
  const v = (cents || 0) / 100;
  try {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: window.FDV?.gifts?.currency || 'MXN',
      minimumFractionDigits: Number.isInteger(v) ? 0 : 2,
    }).format(v);
  } catch {
    return `$${Math.round(v)}`;
  }
}

/* ------------------------------------------------------------- mutaciones -- */

function cartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);
}

async function postJSON(url, body) {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  const text = await res.text();
  let data = {};
  try {
    data = JSON.parse(text);
  } catch {
    /* algunas respuestas de error no son JSON */
  }
  if (!res.ok) {
    const err = new Error(data.description || data.message || `HTTP ${res.status}`);
    err.data = data;
    throw err;
  }
  return data;
}

/* --------------------------------------------------------------- el motor -- */

class GiftEngine {
  #offers = [];
  #timer = null;
  #running = false;
  #rerunQueued = false;
  #ownEvent = false;
  #prevState = [];
  #pending = false; // hay un reconcile agendado o corriendo
  #unlockRaf = 0;

  #listening = false;

  init() {
    const boot = readBootstrap();
    window.FDV = window.FDV || {};
    window.FDV.gifts = window.FDV.gifts || {};
    Object.assign(window.FDV.gifts, {
      state: [],
      currency: boot.currency || 'MXN',
      debug: window.FDV.gifts.debug || boot.debug === true,
      reconcile: () => this.#scheduleReconcile(),
      reload: () => this.#load(false),
      evaluate: (cart) => evaluate(this.#offers, cart),
      merchSubtotal,
    });
    this.#load(true);
  }

  /**
   * (Re)lee las ofertas del <script id="fdv-gift-offers"> y arranca. Idempotente.
   * @param {boolean} initial - true = carga en el page load (gatea el primer
   *   reconcile por carrito no-vacío); false = `reload()` explícito (siempre reconcilia).
   */
  #load(initial) {
    this.#offers = readOffers();
    window.FDV.gifts.offers = this.#offers;
    const boot = readBootstrap();

    if (!this.#offers.length) {
      log('sin ofertas activas — motor en reposo');
      // Si quedan regalos huérfanos en el carrito (una campaña terminó), un
      // reconcile con 0 ofertas los retira. No engancha listeners.
      if (boot.hasGiftLines) this.#scheduleReconcile();
      return;
    }
    log('ofertas activas:', this.#offers.length);

    if (!this.#listening) {
      document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
      // También al aplicar/quitar un código de descuento desde el drawer / la
      // página /cart (`<cart-discount-component>` nativo) → reconciliar por si
      // hay ofertas con `trigger_type: discount_code` y para mantener
      // `fdv-offer-state` fresco.
      document.addEventListener(StandardEvents.cartDiscountUpdate, this.#onCartUpdate);
      this.#listening = true;
    }

    if (!initial) {
      this.#scheduleReconcile();
      return;
    }
    // Reconcile inicial: solo si el carrito no está vacío (evita fetch en cada carga)...
    if ((boot.cartItemCount ?? 0) > 0) this.#scheduleReconcile();
    // ...o si un url_param de la landing puede desbloquear con carrito vacío.
    else if (this.#offers.some((o) => o.triggerType === 'url_param' && urlParamActive(o))) this.#scheduleReconcile();
    // Carrito vacío: publica una línea base (todo bloqueado) SIN pegarle a /cart.js,
    // para sembrar #prevState. Sin esto, el primer cambio de carrito (p. ej. agregar
    // la vela de un par `product_in_cart`) sería el primer publish → `#emitTransitions`
    // lo trata como "primer render" y NO dispara `fdv:gift:unlocked` (ni el toast ni
    // el confeti). Ver `#emitTransitions`.
    else this.#publishBaseline();
  }

  /** Estado base contra un carrito vacío sintético (sin red). Siembra `#prevState`. */
  #publishBaseline() {
    const emptyCart = {
      items: [],
      items_subtotal_price: 0,
      total_price: 0,
      item_count: 0,
      currency: window.FDV?.gifts?.currency,
      cart_level_discount_applications: [],
    };
    this.#publish(evaluate(this.#offers, emptyCart), emptyCart);
  }

  #onCartUpdate = (event) => {
    if (this.#ownEvent) return;
    // Bloquea el carrito YA (antes de esperar la promesa de Horizon). Ver #lockCart.
    this.#lockCart();
    const after = () => this.#scheduleReconcile();
    if (event?.promise?.then) event.promise.then(after).catch(after);
    else after();
  };

  #scheduleReconcile() {
    this.#pending = true;
    this.#lockCart();
    clearTimeout(this.#timer);
    this.#timer = setTimeout(() => this.#reconcile(), DEBOUNCE_MS);
  }

  /**
   * Bloquea los controles de las líneas del carrito mientras el motor reconcilia.
   *
   * Los botones "quitar" / los steppers de Horizon llevan un **índice de línea
   * posicional** horneado por Liquid (`on:click="/onLineItemRemove/{{ item.index
   * | plus: 1 }}"`). Cuando el motor agrega o quita una línea de regalo, todas
   * las posiciones por debajo se corren y el DOM tarda un instante (un
   * `morphSection` asíncrono) en re-renderizar con los índices nuevos. Si el
   * cliente pulsa "quitar" en esa ventana, el índice viejo apunta a otra línea
   * —o se sale de rango (→ `/cart/change.js` responde 422 "línea no válida")—
   * y se borra el ítem equivocado o no se borra nada. Con regalos en el carrito
   * (líneas que el motor añade al TOPE) esto pasa aún pulsando despacio.
   *
   * La clase va en `<html>` (no en `cart-items-component`, que el `morphSection`
   * de Horizon re-sincroniza y le quitaría la clase a media reconciliación) y es
   * independiente de la `cart-items-disabled` de Horizon (que Horizon libera en
   * su propio `finally`, antes de que el motor termine). Ver
   * `snippets/fdv-gift-engine.liquid` para el CSS. Se libera en `#scheduleUnlock`.
   */
  #lockCart() {
    if (this.#unlockRaf) {
      cancelAnimationFrame(this.#unlockRaf);
      this.#unlockRaf = 0;
    }
    document.documentElement.classList.add('fdv-cart-reconciling');
  }

  #unlockCart() {
    document.documentElement.classList.remove('fdv-cart-reconciling');
  }

  /**
   * Libera el lock tras dejar terminar el morphSection de Horizon (dos frames),
   * con un `setTimeout` de respaldo por si el rAF está congelado (pestaña en
   * segundo plano) — el lock nunca debe quedarse pegado.
   */
  #scheduleUnlock() {
    if (this.#unlockRaf) cancelAnimationFrame(this.#unlockRaf);
    const done = () => {
      this.#unlockRaf = 0;
      if (!this.#pending && !this.#running) this.#unlockCart();
    };
    this.#unlockRaf = requestAnimationFrame(() => {
      this.#unlockRaf = requestAnimationFrame(done);
    });
    setTimeout(done, 150);
  }

  async #reconcile() {
    this.#pending = false;
    if (this.#running) {
      this.#rerunQueued = true;
      return;
    }
    this.#running = true;
    try {
      await this.#reconcileOnce();
    } catch (e) {
      warn('reconcile falló (el carrito nativo sigue intacto):', e);
    } finally {
      this.#running = false;
      if (this.#rerunQueued) {
        this.#rerunQueued = false;
        this.#scheduleReconcile();
      } else {
        // Ya consistente (líneas + índices re-horneados por el morph) → liberar.
        this.#scheduleUnlock();
      }
    }
  }

  async #reconcileOnce() {
    const cart = await (await fetch(`${themeRoute('cart_url')}.js`, { headers: { Accept: 'application/json' } })).json();
    let state = evaluate(this.#offers, cart);

    // --- construir el mapa de regalos deseados ---
    const desired = new Map(); // variantId -> { qty, label, offerId, mechanism }
    let blocked = false;
    for (const s of state) {
      if (blocked) continue;
      if (s.status === 'unlocked' && s.giftVariantId && s.mechanism !== 'free_shipping_message') {
        const qty = Math.max(0, s.giftQty || 0);
        if (qty > 0) {
          const prev = desired.get(s.giftVariantId);
          if (!prev || qty > prev.qty) {
            desired.set(s.giftVariantId, { qty, label: s.giftLineLabel, offerId: s.id, mechanism: s.mechanism });
          }
        }
      }
      if (s.status === 'unlocked' && !s.stackable) blocked = true;
    }

    // --- universo controlado = regalos de ofertas + líneas con _gift en el carrito ---
    const controlled = new Set(this.#offers.map((o) => o.giftVariantId).filter(Boolean));
    for (const it of cart.items) if (isGiftLine(it)) controlled.add(it.id);

    // --- diff ---
    const removeOps = [];
    const addOps = [];
    const changeOps = [];
    for (const vid of controlled) {
      const giftLine = cart.items.find((it) => it.id === vid && isGiftLine(it));
      const want = desired.get(vid);
      const wantQty = want ? want.qty : 0;
      const haveQty = giftLine ? giftLine.quantity : 0;

      if (wantQty === 0 && giftLine) {
        removeOps.push({ id: giftLine.key, quantity: 0 });
      } else if (wantQty > 0 && !giftLine) {
        addOps.push({
          id: vid,
          quantity: wantQty,
          properties: { [GIFT_PROP]: want.offerId, [GIFT_LABEL_PROP]: want.label },
        });
      } else if (wantQty > 0 && giftLine && haveQty !== wantQty) {
        changeOps.push({ id: giftLine.key, quantity: wantQty });
      }
    }

    // --- barrido de líneas de regalo huérfanas ---------------------------------
    // Una línea de una variante de regalo `zero_price` SIN `properties._gift` =
    // el cliente la agregó por su cuenta (página del producto a $0, `/cart/add`,
    // un enlace viejo), no el motor. Esas variantes están dedicadas a $0 — nadie
    // las compra a propósito — así que se retiran SIEMPRE, aunque no tengan la
    // property. Si la oferta califica, el diff de arriba ya encoló el alta de la
    // línea "de verdad" (con `_gift`/`_gift_label` → patch #26 la bloquea); si no
    // califica, la línea desaparece. Devuelve el comportamiento que tenía
    // `syncGiftsFromCart` de Impulse con su whitelist de variant IDs.
    //
    // NO cubre `auto_discount` (ahí la variante de regalo es un producto real y
    // vendible — borrarla a ciegas eliminaría algo que el cliente sí quiere
    // comprar; esa mecánica se apoya en `_gift` + las condiciones del descuento
    // nativo, que Shopify hace cumplir en el checkout).
    const zeroPriceGiftVariants = new Set(
      this.#offers.filter((o) => o.mechanism === 'zero_price' && o.giftVariantId).map((o) => o.giftVariantId)
    );
    for (const it of cart.items) {
      if (!isGiftLine(it) && zeroPriceGiftVariants.has(it.id)) {
        removeOps.push({ id: it.key, quantity: 0 });
      }
    }

    const ops = [...removeOps, ...changeOps, ...addOps];
    if (ops.length === 0) {
      // El motor no toca el carrito → los índices de línea del DOM ya son los
      // que dejó el morph de Horizon. Liberar el lock de inmediato (no esperar
      // los 2 frames de `#scheduleUnlock`).
      if (!this.#pending) this.#unlockCart();
      this.#publish(state, cart);
      return;
    }
    if (ops.length > MAX_OPS_PER_CYCLE) {
      warn(`demasiadas ops (${ops.length}) — abortando por seguridad`, ops);
      return;
    }

    log('ops:', ops);

    // --- ejecutar en el mínimo de requests posible ---
    // Antes: N `/cart/change.js` + N `/cart/add.js` + 1 `/cart/update.js` (serial).
    // Ahora: 1 `/cart/add.js` con todos los items (si hay altas) + 1 `/cart/update.js`
    // que aplica las bajas/cambios en un `updates` por line-key Y devuelve el carrito
    // autoritativo + las secciones para el morph. Quitar un regalo = 1 sola request.
    const sectionIds = cartSectionIds();
    const sectionParams = { sections: sectionIds.join(','), sections_url: location.pathname };

    if (addOps.length) {
      try {
        await postJSON(`${themeRoute('cart_add_url')}`, {
          items: addOps.map((op) => ({ id: op.id, quantity: op.quantity, properties: op.properties })),
        });
        for (const op of addOps) {
          window.FDV?.track?.('fdv_gift_added_to_cart', {
            offer_id: op.properties[GIFT_PROP],
            gift_variant_id: op.id,
            qty: op.quantity,
          });
        }
      } catch (e) {
        // p. ej. una variante de regalo sin stock (inventory policy DENY). No
        // bloqueamos las bajas ni el refresco de secciones que siguen.
        warn('alta de regalo(s) falló:', e);
      }
    }

    const updates = {};
    for (const op of removeOps) updates[op.id] = 0;
    for (const op of changeOps) updates[op.id] = op.quantity;

    const finalCart = await postJSON(`${themeRoute('cart_update_url')}`, { updates, ...sectionParams });

    state = evaluate(this.#offers, finalCart);
    const removedBad = this.#annotateAutoDiscount(state, finalCart);
    this.#dispatchCartUpdate(finalCart);
    this.#publish(state, finalCart);
    // Si se retiró un regalo de auto_discount fallido, reconciliar de nuevo
    // para que el DOM y el estado reflejen la remoción.
    if (removedBad) this.#scheduleReconcile();

    // --- limpiar url_param si su oferta ya no está desbloqueada y no hay regalo ---
    for (const s of state) {
      if (s._offer.triggerType === 'url_param' && s.status !== 'unlocked') clearUrlParam(s._offer);
    }
  }

  /**
   * Marca discountApplied para las ofertas auto_discount y retira el regalo si no aplicó.
   * @returns {boolean} true si retiró algún regalo.
   */
  #annotateAutoDiscount(state, cart) {
    let removed = false;
    for (const s of state) {
      if (s.mechanism !== 'auto_discount' || s.status !== 'unlocked' || !s.giftVariantId) continue;
      const line = cart.items.find((it) => it.id === s.giftVariantId && isGiftLine(it));
      if (!line) continue;
      const applied =
        (line.final_line_price ?? line.line_price) <= 0 ||
        (line.line_level_discount_allocations || []).some(
          (a) => !s.autoDiscountTitle || (a.discount_application?.title || '').toUpperCase() === s.autoDiscountTitle
        );
      s.discountApplied = applied;
      if (!applied) {
        s.error = 'auto_discount_no_aplicado';
        warn(`auto_discount no aplicó para "${s.internalName}" — retirando el regalo`);
        removed = true;
        postJSON(`${themeRoute('cart_change_url')}`, { id: line.key, quantity: 0 }).catch(() => {});
        window.FDV?.track?.('fdv_gift_error', { offer_id: s.id, auto_discount_title: s.autoDiscountTitle });
      }
    }
    return removed;
  }

  #dispatchCartUpdate(cart) {
    const sections = cart.sections || {};
    // El validador de Standard Events exige `lines` con >=1 elemento y quantity > 0.
    // Reportamos el carrito resultante (los listeners de Horizon usan `detail.sections`,
    // no el contenido de `lines`).
    const lines = (cart.items || [])
      .filter((i) => i.quantity > 0)
      .map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity }));
    if (!lines.length) {
      // Carrito vacío tras la reconciliación (p. ej. quitaste el último producto
      // pagado y solo quedaba el regalo emparejado, que el motor retira). El
      // `CartLinesUpdateEvent` exige `lines` no vacío → no podemos despacharlo,
      // así que `component-cart-items` nunca morfea y la línea de regalo ya
      // borrada en el server sigue pintada hasta el siguiente cambio de carrito.
      // Morfeamos las secciones del carrito nosotros para reflejar el vacío.
      for (const [id, html] of Object.entries(sections)) {
        if (typeof html === 'string') {
          try {
            morphSection(id, html);
          } catch (e) {
            warn('morphSection (carrito vacío) falló para', id, e);
          }
        }
      }
      // El ícono del carrito (contador burbuja) también escucha `cartLinesUpdate`
      // → sin evento se queda en la cuenta vieja. Lo ponemos a 0 a mano.
      for (const icon of document.querySelectorAll('cart-icon')) {
        try {
          icon.renderCartBubble?.(0);
        } catch {
          /* noop */
        }
      }
      return;
    }
    const deferred = CartLinesUpdateEvent.createPromise();
    this.#ownEvent = true;
    try {
      document.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'update',
          context: 'cart',
          lines,
          promise: deferred.promise,
        })
      );
    } finally {
      // dejar pasar el turno de microtareas antes de volver a escuchar
      Promise.resolve().then(() => {
        this.#ownEvent = false;
      });
    }
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { sections, source: 'fdv-gift-engine', didError: false },
    });
  }

  #publish(state, cart) {
    window.FDV.gifts.state = state;
    window.FDV.gifts.cart = cart;
    document.dispatchEvent(new CustomEvent('fdv:gifts:state', { detail: { state, cart } }));
    this.#emitTransitions(this.#prevState, state, cart);
    this.#prevState = state.map((s) => ({ id: s.id, status: s.status }));
  }

  #emitTransitions(prev, next, cart) {
    const track = window.FDV?.track;
    const prevById = new Map(prev.map((s) => [s.id, s.status]));
    for (const s of next) {
      const was = prevById.get(s.id);
      if (was === undefined) continue; // primer render, no es transición
      if (was !== 'unlocked' && s.status === 'unlocked') {
        // Evento DOM para las superficies de UI (celebración, etc.).
        document.dispatchEvent(new CustomEvent('fdv:gift:unlocked', { detail: { offer: s, cart } }));
        track?.('fdv_gift_unlocked', {
          offer_id: s.id,
          trigger_type: s._offer.triggerType,
          gift_variant_id: s.giftVariantId,
          mechanism: s.mechanism,
          threshold: s.threshold,
          cart_subtotal: cart.items_subtotal_price,
        });
        if (s._offer.triggerType === 'discount_code' || s._offer.triggerType === 'url_param') {
          track?.('fdv_promo_claimed', {
            offer_id: s.id,
            code: s._offer.discountCode || null,
            param: s._offer.urlParam || null,
          });
        }
      } else if (was === 'unlocked' && s.status !== 'unlocked') {
        document.dispatchEvent(new CustomEvent('fdv:gift:removed', { detail: { offer: s, cart } }));
        track?.('fdv_gift_removed', {
          offer_id: s.id,
          gift_variant_id: s.giftVariantId,
          cart_subtotal: cart.items_subtotal_price,
        });
      }
    }
  }
}

function themeRoute(key) {
  const r = window.Theme?.routes || {};
  const map = {
    cart_url: r.cart_url || '/cart',
    cart_add_url: r.cart_add_url || '/cart/add.js',
    cart_change_url: r.cart_change_url || '/cart/change.js',
    cart_update_url: r.cart_update_url || '/cart/update.js',
  };
  return map[key];
}

/* -------------------------------------------------------------------- init -- */

const engine = new GiftEngine();
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => engine.init(), { once: true });
} else {
  engine.init();
}
